import React from 'react';
import { Tooltip, ControlVariant, TooltipRefObject } from 'dig-components/tooltips';
import { Title, Text } from 'dig-components/typography';
import { Button } from 'dig-components/buttons';
import { OverlayPlacement } from 'dig-components/overlay';
import { useEventListener, useLatestRef } from 'hellospa/common/hooks';
import { useAppContext } from 'hellospa/app-context';
import styles from './styles.scss';

const tourContext = React.createContext<string[]>([]);
export const TourContextProvider = tourContext.Provider;

/**
 * `tourItem` is the `data-tour-item={whatever}` that needs to be attached to tour
 * targets.
 */
export type Step = {
  tourItem: string;
  content: JSX.Element
    | (({ nextStep, prevStep }: { nextStep: () => void, prevStep: () => void }) => JSX.Element);
  cta?: JSX.Element,
  title?: JSX.Element,
  placement?: OverlayPlacement;
  variant?: ControlVariant;
  disableAdvanceOnTargetClick?: boolean;
}

type TourProps = {
  steps: Array<Step | undefined>;
  tourName: string;
}

type TourTooltipProps = {
  idx?: number,
  step?: Step,
  open: boolean,
  variant?: ControlVariant,
  prevStep: () => void,
  nextStep: () => void,
  disableAdvanceOnTargetClick?: boolean,
}

function useDelegatedClick(
  target: Element | null,
  callback: (event: Event) => void,
) {
  const targetRef = useLatestRef(target);

  useEventListener(document.body, 'mousedown', (event) => {
    if (targetRef.current && event.target) {
      const clickedInTarget = targetRef.current.contains(event.target as Node);
      if (clickedInTarget) {
        callback(event);
      }
    }
  });
}

const TourTooltip = ({
  idx,
  step,
  open,
  variant,
  prevStep,
  nextStep,
  disableAdvanceOnTargetClick,
}: TourTooltipProps) => {

  const [target, setTarget] = React.useState<Element | null>(null);

  const ref = React.useRef<TooltipRefObject>(null);
  const triggerRef = useLatestRef(target);

  React.useEffect(() => {
    const domSelector = step !== null
      ? `[data-tour-item="${step?.tourItem}"]`
      : '';

    const t = document.querySelector(domSelector);
    triggerRef.current = t;
    setTarget(t);

    /**
     * This interval is necessary to deal with a FOUC
     * issue where styling is not yet loaded, and the tooltip
     * is positioned incorrectly. It runs for 1 second, and the
     * interval exists until the Tour component unmounts.
     */
    let count = 0;
    const timer = setInterval(() => {
      if (count++ < 5) {
        ref?.current?.update();
      } else {
        // Need this to clean up after count reaches 5.
        // Otherwise timer will only be removed on unmount.
        clearInterval(timer);
      }
    }, 200);

    // Cleanup timer when component unmounts
    return function onCleanup() {
      clearInterval(timer);
    };
  }, [step, triggerRef]);

  useDelegatedClick(target, () => {
    if (!disableAdvanceOnTargetClick && open) {
      nextStep();
    }
  });

  const placement: Step['placement'] = step?.placement ?? 'bottom';

  if (!step) {
    return null;
  }

  return (
    <div data-qa-ref={`tour-tooltip-${idx}`}>
      <Tooltip.Control
        ref={ref}
        placement={placement}
        triggerRef={triggerRef}
        variant={variant || undefined}
        open={open}
        shouldReturnFocus
        auto={false}
      >
        {step.title
        && <Title
          size="standard"
          inverse
          data-qa-ref="tour-tooltip-title"
        >
          {step.title}
        </Title>
      }

        <Text
          tagName="p"
          color="standard"
          inverse
          data-qa-ref="tour-tooltip-text"
        >
          {typeof step.content === 'function' ? (
            step.content({ nextStep, prevStep })
          ) : step.content}
        </Text>

        {step.cta
        && <div className={styles.footer}>
          <Button
            variant="outline"
            inverse
            onClick={nextStep}
            autoFocus
            data-qa-ref="tour-tooltip-cta"
          >
            {step.cta}
          </Button>
        </div>
      }
      </Tooltip.Control>
    </div>
  );
};

function Tour({
  steps,
  tourName,
}: TourProps) {
  const { tours } = useAppContext();
  const disabledTours = React.useContext(tourContext);
  const isTourEnabled = !disabledTours.includes(tourName);

  // Step State
  type StepState = {
    stepIndex: number,
    done: boolean,
  }

  const [state, setStep] = React.useState<StepState>({
    stepIndex: 0,
    done: false,
  });

  // steps is really an Array<Steps>, but I needed to add the undefined to its
  // type to make TS recognize that currentStep may be undefined after
  // completing last step
  const currentStep = steps[state.stepIndex];

  const numSteps = steps.length;

  // When tour completes...
  React.useEffect(() => {
    if (state.stepIndex === steps.length && !state.done) {
      setStep((state) => ({ ...state, done: true }));
      tours.completeTour(tourName);
    }
  }, [numSteps, state, steps.length, tourName, tours]);

  const nextStep = React.useCallback(() => {
    setStep((prevState) => {
      const stepIndex = prevState.stepIndex + 1;
      return {
        ...prevState,
        stepIndex,
      };
    });
  }, []);

  const prevStep = React.useCallback(() => {
    setStep((prevState) => ({
      ...prevState,
      stepIndex: Math.max(0, prevState.stepIndex - 1),
    }));
  }, []);

  if (!currentStep || !isTourEnabled) {
    return null;
  }

  return (
    <TourTooltip
      key={state.stepIndex}
      idx={state.stepIndex}
      step={currentStep}
      variant={currentStep?.variant}
      nextStep={nextStep}
      prevStep={prevStep}
      disableAdvanceOnTargetClick={currentStep?.disableAdvanceOnTargetClick}
      open />
  );
}

export default Tour;

export function buildTourSteps<S extends string>(
  tourName: string,
  steps: Array<S>,
): Record<S, string> {
  // I need to cast this because TS recognizes it doesn't contain all the keys
  // yet.
  const obj = {} as Record<S, string>;
  steps.forEach(
    (stepName) => { obj[stepName] = `${tourName}.${stepName}`; },
  );

  return obj;
}
